import { useEffect, useState } from 'react';
import { BarChartOutlined, KeyOutlined, RobotOutlined, SafetyCertificateOutlined, WalletOutlined } from '@ant-design/icons';
import { Alert, App, AutoComplete, Button, Form, Input, InputNumber, Popconfirm, Select, Switch, Tag } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
const MODELS = [
    { value: 'claude-sonnet-5-5', label: 'claude-sonnet-5-5 · balanced (recommended)' },
    { value: 'claude-opus-5-5', label: 'claude-opus-5-5 · most capable' },
    { value: 'claude-haiku-5-5', label: 'claude-haiku-5-5 · fastest, lowest cost' }
];
const FALLBACK_BILL_PROVIDERS = [{ id: 'gemini', label: 'Google Gemini', models: ['gemini-3.6-flash', 'gemini-3.5-flash', 'gemini-3.5-flash-lite', 'gemini-3.8-flash'] }];
export function AiSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [dirty, setDirty] = useState(false);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/ai').then((body) => body.settings));
    const bill = useLoad(() => api('/v1/admin/settings/ai-bill').then((body) => body.settings));
    const settings = data;
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ apiKey: '', model: settings.model, monthlyLimit: settings.monthlyLimit });
        setDirty(false);
    }, [settings, form]);
    async function save(values, clearKey = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/ai', { method: 'PUT', body: JSON.stringify({ ...values, clearKey }) });
            message.success(clearKey ? 'Key removed. AI reading is off.' : body.settings.apiKeySet ? 'Saved. AI reading is on.' : 'Saved');
            setTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function runTest() {
        setTesting(true);
        setTest(null);
        try {
            await api('/v1/admin/settings/ai/test', { method: 'POST', body: JSON.stringify({ apiKey: form.getFieldValue('apiKey') ?? '' }) });
            setTest({ ok: true, text: 'Anthropic accepted the key.' });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The key could not be checked' });
        }
        finally {
            setTesting(false);
        }
    }
    const on = Boolean(settings?.apiKeySet);
    const billOn = Boolean(bill.data?.ready);
    return (<>
      <PageHeader title="AI reading" description="Claude reads bank statements and invoice PDFs. Handwritten bill photos are read by a separate provider (Gemini first) and charged to the customer's wallet." actions={<BackToSettings />}/>
      <ErrorNotice error={error || bill.error}/>
      {settings?.keyUnreadable && (<Alert type="warning" showIcon title="The saved Anthropic API key cannot be read on this server" description="The server's settings key was replaced or lost, so AI reading is off until the key is entered again." style={{ marginBottom: 20 }}/>)}
      {bill.data?.keyUnreadable && (<Alert type="warning" showIcon title="The saved bill-image API key cannot be read on this server" description="The server's settings key was replaced or lost. Enter the key again to turn bill photo reading back on." style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="Statements & PDFs" tone={on ? 'green' : 'slate'} icon={<RobotOutlined />} loading={loading && !data} value={on ? 'On' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Bill photos" tone={billOn ? 'green' : 'slate'} icon={<WalletOutlined />} loading={bill.loading && !bill.data} value={billOn ? 'On' : 'Off'} foot={bill.data?.updatedAt ? `Changed ${timeAgo(bill.data.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Claude model" tone="indigo" icon={<SafetyCertificateOutlined />} loading={loading && !data} value={<span style={{ fontSize: 18 }}>{settings?.model ?? '—'}</span>} foot={`${settings?.monthlyLimit ?? '—'} readings a month`}/>
        <StatCard label="Per-image charge" tone="amber" icon={<BarChartOutlined />} loading={bill.loading && !bill.data} value={bill.data?.amountPaise == null ? '—' : `₹${(bill.data.amountPaise / 100).toFixed(2)}`} foot="Taken from the cloud wallet"/>
      </div>

      {loading && !data ? (<Panel flush title="Anthropic" description="Uses the Claude API with this server's key.">
          <FormSkeleton sections={2}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => void save(values)} onValuesChange={() => setDirty(true)}>
        <Panel flush title="Anthropic" description="Uses the Claude API with this server's key." extra={on ? <Tag color="success" variant="filled"><span className="status-dot"/>On</Tag> : <Tag variant="filled">Off</Tag>}>
          <div className="form-section">
            <div>
              <h4>API key</h4>
              <p>Create a key in the Anthropic Console (console.anthropic.com). It is stored encrypted and never shown again.</p>
            </div>
            <div>
              <Form.Item label="Anthropic API key" name="apiKey" extra={on ? <span><KeyOutlined style={{ color: 'var(--brand)' }}/> A key ending <span className="mono">{settings?.apiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Without a key, AI reading is off for every customer.'}>
                <Input.Password autoComplete="new-password" placeholder={on ? '••••••••••••' : 'sk-ant-…'} className="mono"/>
              </Form.Item>
              {on && (<Popconfirm title="Remove the saved key?" description="AI reading stops for every customer until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(form.getFieldsValue(), true)}>
                  <Button danger type="link" style={{ paddingInline: 0 }}>Remove key</Button>
                </Popconfirm>)}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Model and usage</h4>
              <p>Changes apply to the next reading; no restart needed.</p>
            </div>
            <div className="form-row">
              <Form.Item label="Model" name="model" rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9.-]{2,79}$/, message: 'Enter a model ID such as claude-sonnet-5-5.' }]}>
                <AutoComplete placeholder="Choose or type a model" options={MODELS} className="mono"/>
              </Form.Item>
              <Form.Item label="Readings per account each month" name="monthlyLimit" rules={[{ required: true }]}>
                <InputNumber placeholder="100" min={1} max={100000} style={{ width: '100%' }}/>
              </Form.Item>
            </div>
          </div>

          {test && (<div style={{ padding: '0 20px 20px' }}>
              <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
            </div>)}
          <div className="form-actions">
            <Button onClick={() => void runTest()} loading={testing}>Test key</Button>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save changes</Button>
          </div>
        </Panel>
      </Form>)}

      <BillImageSettings loaded={bill}/>
    </>);
}

function BillImageSettings({ loaded }) {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [dirty, setDirty] = useState(false);
    const settings = loaded.data;
    const providers = settings?.providers?.length ? settings.providers : FALLBACK_BILL_PROVIDERS;
    const provider = Form.useWatch('provider', form) || settings?.provider || 'gemini';
    const modelOptions = (providers.find((row) => row.id === provider)?.models ?? []).map((value) => ({ value, label: value }));
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({
            enabled: settings.enabled,
            provider: settings.provider,
            apiKey: '',
            model: settings.model,
            amountRupees: settings.amountPaise == null ? 5 : settings.amountPaise / 100
        });
        setDirty(false);
    }, [settings, form]);
    async function save(values, clearKey = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/ai-bill', {
                method: 'PUT',
                body: JSON.stringify({
                    enabled: values.enabled,
                    provider: values.provider,
                    apiKey: values.apiKey,
                    model: values.model,
                    amountPaise: Math.round(Number(values.amountRupees) * 100),
                    clearKey
                })
            });
            message.success(clearKey ? 'Key removed. Bill photo reading is off.' : body.settings.ready ? 'Saved. Bill photo reading is on.' : 'Saved');
            setTest(null);
            await loaded.reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function runTest() {
        setTesting(true);
        setTest(null);
        try {
            await api('/v1/admin/settings/ai-bill/test', { method: 'POST', body: JSON.stringify({ apiKey: form.getFieldValue('apiKey') ?? '', provider: form.getFieldValue('provider') ?? 'gemini' }) });
            setTest({ ok: true, text: 'Gemini accepted the key.' });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The key could not be checked' });
        }
        finally {
            setTesting(false);
        }
    }
    const on = Boolean(settings?.ready);
    if (loaded.loading && !settings) {
        return (<Panel flush title="Bill photos" description="Handwritten and photographed bills, charged to the customer's wallet.">
        <FormSkeleton sections={2}/>
      </Panel>);
    }
    return (<Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => void save(values)} onValuesChange={() => setDirty(true)} style={{ marginTop: 20 }}>
      <Panel flush title="Bill photos" description="The desktop Import hub sends handwritten and photographed bills here. Each image is charged once from the connected cloud account wallet, the same way GST APIs are." extra={on ? <Tag color="success" variant="filled"><span className="status-dot"/>On</Tag> : <Tag variant="filled">Off</Tag>}>
        <div className="form-section">
          <div>
            <h4>Enable</h4>
            <p>Switch off to stop every customer from reading bill photos, without deleting the key.</p>
          </div>
          <Form.Item label="Bill photo reading" name="enabled" valuePropName="checked">
            <Switch checkedChildren="On" unCheckedChildren="Off"/>
          </Form.Item>
        </div>

        <div className="form-section">
          <div>
            <h4>Provider and key</h4>
            <p>Gemini is available now. Another provider can be added later without changing the desktop app.</p>
          </div>
          <div>
            <div className="form-row">
              <Form.Item label="Provider" name="provider" rules={[{ required: true }]}>
                <Select options={providers.map((row) => ({ value: row.id, label: row.label }))}/>
              </Form.Item>
              <Form.Item label="Model" name="model" rules={[{ required: true, pattern: /^[a-zA-Z0-9][a-zA-Z0-9._-]{2,79}$/, message: 'Enter a model ID such as gemini-3.6-flash.' }]}>
                <AutoComplete placeholder="Choose or type a model" options={modelOptions} className="mono"/>
              </Form.Item>
            </div>
            <Form.Item label="API key" name="apiKey" extra={settings?.apiKeySet ? <span><KeyOutlined style={{ color: 'var(--brand)' }}/> A key ending <span className="mono">{settings?.apiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Create a key in Google AI Studio (aistudio.google.com). It is stored encrypted and never shown again.'}>
              <Input.Password autoComplete="new-password" placeholder={settings?.apiKeySet ? '••••••••••••' : 'AIza…'} className="mono"/>
            </Form.Item>
            {settings?.apiKeySet && (<Popconfirm title="Remove the saved key?" description="Bill photo reading stops for every customer until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(form.getFieldsValue(), true)}>
                <Button danger type="link" style={{ paddingInline: 0 }}>Remove key</Button>
              </Popconfirm>)}
          </div>
        </div>

        <div className="form-section">
          <div>
            <h4>Wallet charge</h4>
            <p>Charged from the customer's Kivonex wallet on a successful read. A failed read is not charged. Account overrides stay on Wallet & Usage.</p>
          </div>
          <Form.Item label="Price per image (₹)" name="amountRupees" rules={[{ required: true }]}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }}/>
          </Form.Item>
        </div>

        {test && (<div style={{ padding: '0 20px 20px' }}>
            <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
          </div>)}
        <div className="form-actions">
          <Button onClick={() => void runTest()} loading={testing}>Test key</Button>
          <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save bill photo API</Button>
        </div>
      </Panel>
    </Form>);
}
